import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faCog,
    faInfoCircle,
    faEnvelope,
    faShieldAlt,
    faFileContract,
    faSignOutAlt,
    faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router';
import { useAuth } from '../../contexts/authContext';
import { auth } from '../../firebase/firebase';
import logo from '../../images/GoldfinderLogo.svg';

/**
 * Floating Logo Control
 * Displays the Gold Finder logo in the top-left corner of the map
 */
export function FloatingLogo() {
    return (
        <div className="gf-logo-control">
            <a href="/">
                <img src={logo} alt="Gold Finder" />
            </a>
        </div>
    );
}

/**
 * Floating Menu Control
 * Displays a user menu button with dropdown for navigation
 */
export function FloatingMenu({ onOpenAccountSettings }) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const { userLoggedIn } = useAuth();

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    return (
        <div
            className={`gf-menu-control ${isOpen ? 'open' : ''}`}
            ref={menuRef}
        >
            <button
                className="gf-menu-button"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Open menu"
                aria-expanded={isOpen}
            >
                <FontAwesomeIcon icon={faUser} />
            </button>

            <div className="gf-menu-dropdown">
                <button
                    className="gf-menu-item"
                    onClick={() => {
                        onOpenAccountSettings();
                        setIsOpen(false);
                    }}
                >
                    <FontAwesomeIcon icon={faCog} />
                    Account Settings
                </button>

                <div className="gf-menu-divider" />

                <a
                    className="gf-menu-item"
                    href="https://goldfinder.net.au/faqs"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <FontAwesomeIcon icon={faInfoCircle} />
                    More Info
                </a>

                <a
                    className="gf-menu-item"
                    href="mailto:support@goldfinder.net.au"
                >
                    <FontAwesomeIcon icon={faEnvelope} />
                    Contact Support
                </a>

                <div className="gf-menu-divider" />

                <a
                    className="gf-menu-item"
                    href="/privacy"
                >
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Privacy Policy
                </a>

                <a
                    className="gf-menu-item"
                    href="/terms"
                >
                    <FontAwesomeIcon icon={faFileContract} />
                    Terms & Conditions
                </a>

                {userLoggedIn && (
                    <>
                        <div className="gf-menu-divider" />
                        <button
                            className="gf-menu-item"
                            onClick={handleLogout}
                        >
                            <FontAwesomeIcon icon={faSignOutAlt} />
                            Log Out
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Sidebar Toggle Button
 * Floating button to show/hide the sidebar
 */
export function SidebarToggle({ isSideBarShown, onToggle }) {
    return (
        <button
            className={`gf-sidebar-toggle ${!isSideBarShown ? 'sidebar-hidden' : ''}`}
            onClick={onToggle}
            title={isSideBarShown ? 'Hide Layer Controls' : 'Show Layer Controls'}
            aria-label={isSideBarShown ? 'Hide Layer Controls' : 'Show Layer Controls'}
        >
            <FontAwesomeIcon icon={faLayerGroup} />
        </button>
    );
}

/**
 * Mobile Sidebar Toggle Button
 * Floating button to show/hide the mobile layers panel (visible only on small screens)
 */
export function MobileSidebarToggle({ isOpen, onToggle }) {
    const handleClick = () => {
        console.log('[MobileSidebarToggle] Button clicked, isOpen:', isOpen);
        if (onToggle) {
            onToggle();
        } else {
            console.warn('[MobileSidebarToggle] onToggle is not defined!');
        }
    };

    return (
        <button
            className={`gf-mobile-sidebar-toggle ${isOpen ? 'open' : ''}`}
            onClick={handleClick}
            title={isOpen ? 'Hide Layer Controls' : 'Show Layer Controls'}
            aria-label={isOpen ? 'Hide Layer Controls' : 'Show Layer Controls'}
        >
            <FontAwesomeIcon icon={faLayerGroup} />
        </button>
    );
}

export default FloatingMenu;
